import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Navbar,
  Nav,
  Image,
  Modal,
  Dropdown,
} from 'react-bootstrap';
import { apifetch } from '../services/interceptors';
import { useNavigate } from 'react-router-dom';
import { useTheme } from "../ThemeContext";
import {
  FaBug,
  FaPaperclip,
  FaCheckCircle,
  FaTimes,
  FaUpload,
  FaExclamationTriangle,
  FaDownload,
  FaMoon,
  FaSun,
  FaHome,
  FaUser,
  FaSignInAlt,
  FaUserPlus,
  FaBars,
  FaCog
} from 'react-icons/fa';

const BugReportForm = () => {
  const { isDarkMode, setIsDarkMode } = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    steps: '',
    attachment: null,
  });
  const [fileError, setFileError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Check screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check Authentication
  useEffect(() => {
    const validateSession = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setNewUser(true)
      } else {
        setNewUser(false);
      }
      setLoading(false);
    };

    validateSession();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateFile = (file) => {
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    if (file.size > MAX_FILE_SIZE) {
      setFileError(`"${file.name}" exceeds 10MB limit. Please choose a smaller file.`);
      return false;
    }
    return true;
  };

  const handleFileSelect = (file) => {
    setFileError('');

    if (validateFile(file)) {
      setFormData((prev) => ({ ...prev, attachment: file }));
    } else {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Drag and Drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      if (files.length > 1) {
        setFileError('Please upload only one file at a time.');
        return;
      }

      const file = files[0];
      handleFileSelect(file);
    }
  };

  const removeFile = () => {
    setFormData((prev) => ({ ...prev, attachment: null }));
    setFileError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadAreaClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    if (formData.attachment) {
      const MAX_FILE_SIZE = 10 * 1024 * 1024;
      if (formData.attachment.size > MAX_FILE_SIZE) {
        setError('The selected file exceeds the 10MB size limit');
        setSubmitting(false);
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');
      const data = new FormData();

      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('steps', formData.steps);
      data.append('category', 'Bug');
      data.append('priority', 'medium');

      if (formData.attachment) {
        data.append('attachments', formData.attachment);
      }

      const response = await apifetch('s1/api/requests', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: data,
      });

      const result = await response.json();
      if (response.ok) {
        setSubmitted(true);
        setFormData({ title: '', description: '', steps: '', attachment: null });
        setFileError('');
      } else {
        setError(result.error || 'Failed to submit bug report.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100" style={{
        background: isDarkMode
          ? 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div className="text-center text-white">
          <Spinner animation="border" variant="light" />
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: isDarkMode
        ? 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)'
        : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    }}>
      {/* Modern Navbar */}
      <Navbar
        expand="lg"
        fixed="top"
        className={`custom-navbar ${isDarkMode ? 'navbar-dark' : 'navbar-light'}`}
        style={{
          backdropFilter: 'blur(20px)',
          backgroundColor: isDarkMode ? 'rgba(15, 15, 15, 0.8)' : 'rgba(255, 255, 255, 0.8)',
          borderBottom: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
          padding: '0.5rem 0'
        }}
      >
        <Container>
          <Navbar.Brand className="d-flex align-items-center brand-logo cursor-pointer" onClick={() => navigate('/')}>
            <Image
              src="images/logo_nb.png"
              alt="Pipsqueak Logo"
              width="32"
              height="32"
              className="me-2"
            />
            <span className="fw-bold" style={{
              fontSize: '1.5rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Pipsqueak
            </span>
          </Navbar.Brand>

          <Navbar.Toggle aria-controls="basic-navbar-nav">
            <FaBars />
          </Navbar.Toggle>

          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto">
            </Nav>

            <Nav className="d-flex align-items-center gap-2">
              {/* Theme Toggle */}
              <Button
                variant="outline-light"
                onClick={() => setIsDarkMode(prev => !prev)}
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: '40px',
                  height: '40px',
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                  background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
                }}
              >
                {isDarkMode ? <FaSun size={16} /> : <FaMoon size={16} color='black' />}
              </Button>

              {/* User Actions */}
              {newUser ? (
                <>
                  {!isMobile ? (
                    <>
                      <Button
                        variant="outline-primary"
                        onClick={() => navigate('/login')}
                        className="rounded-pill px-3"
                        style={{
                          border: '2px solid',
                          fontWeight: '600'
                        }}
                      >
                        <FaSignInAlt className="me-2" />
                        Login
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => navigate('/register')}
                        className="rounded-pill px-4"
                        style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          border: 'none',
                          fontWeight: '600'
                        }}
                      >
                        <FaUserPlus className="me-2" />
                        Get Started
                      </Button>
                    </>
                  ) : (
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="outline-light"
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: '40px',
                          height: '40px',
                          border: isDarkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                          background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
                        }}
                      >
                        <FaUser size={16} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu className={isDarkMode ? 'bg-dark border-light' : ''}>
                        <Dropdown.Item
                          onClick={() => navigate('/login')}
                          className={isDarkMode ? 'text-light' : ''}
                        >
                          <FaSignInAlt className="me-2" />
                          Login
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => navigate('/register')}
                          className={isDarkMode ? 'text-light' : ''}
                        >
                          <FaUserPlus className="me-2" />
                          Get Started
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </>
              ) : (
                <>
                  {!isMobile ? (
                    <Button
                      variant="outline-primary"
                      onClick={() => navigate('/dashboard')}
                      className="rounded-pill px-3"
                      style={{
                        border: '2px solid',
                        fontWeight: '600'
                      }}
                    >
                      <FaCog className="me-2" />
                      Dashboard
                    </Button>
                  ) : (
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="outline-light"
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: '40px',
                          height: '40px',
                          border: isDarkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                          background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
                        }}
                      >
                        <FaUser size={16} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu className={isDarkMode ? 'bg-dark border-light' : ''}>
                        <Dropdown.Item
                          onClick={() => navigate('/dashboard')}
                          className={isDarkMode ? 'text-light' : ''}
                        >
                          <FaCog className="me-2" />
                          Dashboard
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => navigate('/')}
                          className={isDarkMode ? 'text-light' : ''}
                        >
                          <FaHome className="me-2" />
                          Home
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Main Content */}
      <Container className="py-5 mt-5" style={{ paddingTop: '80px' }}>
        <Row className="justify-content-center align-items-center min-vh-100">
          <Col md={10} lg={8} xl={6}>
            <Card
              className="shadow-2xl border-0"
              style={{
                borderRadius: '24px',
                backdropFilter: 'blur(20px)',
                background: isDarkMode
                  ? 'rgba(30, 30, 30, 0.8)'
                  : 'rgba(255, 255, 255, 0.9)',
                border: isDarkMode
                  ? '1px solid rgba(255,255,255,0.1)'
                  : '1px solid rgba(255,255,255,0.5)'
              }}
            >
              <Card.Header
                className="border-0 text-center py-4 position-relative overflow-hidden"
                style={{
                  borderTopLeftRadius: '24px',
                  borderTopRightRadius: '24px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                }}
              >
                <div className="position-absolute w-100 h-100 top-0 start-0 opacity-10">
                  <div style={{
                    background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.3) 0%, transparent 50%)',
                    width: '100%',
                    height: '100%'
                  }}></div>
                </div>
                <div className="d-flex align-items-center justify-content-center text-white position-relative">
                  <div className="p-3 rounded-circle me-3" style={{
                    background: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)'
                  }}>
                    <FaBug size={28} />
                  </div>
                  <div>
                    <h3 className="mb-0 fw-bold">Report a Bug</h3>
                    <p className="mb-0 mt-2 text-white-50">
                      Help us improve by reporting issues you encounter
                    </p>
                  </div>
                </div>
              </Card.Header>

              <Card.Body className={`p-4 p-md-5 ${isDarkMode ? "text-light" : ""}`}>
                {error && (
                  <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError('')}
                    className="border-0 rounded-3 shadow-sm mb-4"
                    style={{
                      background: 'rgba(220, 53, 69, 0.1)',
                      border: '1px solid rgba(220, 53, 69, 0.2)'
                    }}
                  >
                    <FaExclamationTriangle className="me-2" />
                    {error}
                  </Alert>
                )}

                {fileError && (
                  <Alert
                    variant="warning"
                    dismissible
                    onClose={() => setFileError('')}
                    className="border-0 rounded-3 shadow-sm mb-4"
                    style={{
                      background: 'rgba(255, 193, 7, 0.1)',
                      border: '1px solid rgba(255, 193, 7, 0.2)'
                    }}
                  >
                    <FaExclamationTriangle className="me-2" />
                    {fileError}
                  </Alert>
                )}

                {submitted ? (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <div
                        className="rounded-circle d-inline-flex align-items-center justify-content-center"
                        style={{
                          width: '100px',
                          height: '100px',
                          background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                          boxShadow: '0 8px 32px rgba(40, 167, 69, 0.3)'
                        }}
                      >
                        <FaCheckCircle size={42} className="text-white" />
                      </div>
                    </div>
                    <h4 className="fw-bold mb-3">Bug Reported Successfully!</h4>
                    <p className="text-muted mb-4">
                      Thank you for your feedback. Our team will review it and get back to you soon.
                    </p>
                    <Button
                      variant="primary"
                      onClick={() => navigate('/features')}
                      className="px-5 py-3 rounded-3 fw-semibold"
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
                      }}
                    >
                      Back to Feature Board
                    </Button>
                  </div>
                ) : (
                  <Form onSubmit={handleSubmit}>
                    {/* Title Field */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold mb-3 fs-6">Bug Title</Form.Label>
                      <Form.Control
                        type="text"
                        name="title"
                        placeholder="Brief, descriptive title of the bug"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        className={`py-3 px-4 rounded-3 border-1 fs-6 ${isDarkMode ? "placeholder-light" : ""}`}
                        style={{
                          background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                          border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                          color: isDarkMode ? '#fff' : '#000',
                          fontSize: '1rem'
                        }}
                      />
                    </Form.Group>

                    {/* Description Field */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold mb-3 fs-6">Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="description"
                        placeholder="Describe what happened, when it occurs, and the impact..."
                        value={formData.description}
                        onChange={handleChange}
                        required
                        className={`py-3 px-4 rounded-3 border-1 fs-6 ${isDarkMode ? "placeholder-light" : ""}`}
                        style={{
                          background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                          border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                          color: isDarkMode ? '#fff' : '#000',
                          resize: 'vertical'
                        }}
                      />
                    </Form.Group>

                    {/* Steps to Reproduce */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold mb-3 fs-6">
                        Steps to Reproduce
                        <span className="fw-normal"> (Optional)</span>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="steps"
                        placeholder="1. Go to...&#10;2. Click on...&#10;3. Observe..."
                        value={formData.steps}
                        onChange={handleChange}
                        className={`py-3 px-4 rounded-3 border-1 fs-6 ${isDarkMode ? "placeholder-light" : ""}`}
                        style={{
                          background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                          border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                          color: isDarkMode ? '#fff' : '#000',
                          resize: 'vertical'
                        }}
                      />
                    </Form.Group>

                    {/* File Upload - Modern Style with Drag & Drop */}
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold mb-3 fs-6 d-block">
                        <FaPaperclip className="me-2" />
                        Attachment
                        <span className="fw-normal text-muted"> (Optional)</span>
                      </Form.Label>

                      {/* Hidden file input */}
                      <Form.Control
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileInputChange}
                        className="d-none"
                        accept="*/*"
                      />

                      {/* Modern File Upload Area */}
                      {!formData.attachment ? (
                        <div
                          className={`text-center p-5 border-2 rounded-3 ${isDragging
                            ? 'border-primary bg-primary bg-opacity-10'
                            : isDarkMode
                              ? 'border-secondary bg-dark border-dashed placeholder-light'
                              : 'border-light bg-light border-dashed'
                            }`}
                          style={{
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            borderStyle: isDragging ? 'solid' : 'dashed',
                            background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'
                          }}
                          onClick={handleUploadAreaClick}
                          onDragEnter={handleDragEnter}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                        >
                          <FaUpload
                            size={32}
                            className={`mb-3 ${isDragging ? 'text-primary' : isDarkMode ? 'text-secondary' : 'text-muted'}`}
                          />
                          <h5 className={isDragging ? 'text-primary' : isDarkMode ? 'text-light' : 'text-dark'}>
                            {isDragging ? 'Drop file here' : 'Drop file here or click to upload'}
                          </h5>
                          <p className={`small ${isDragging ? 'text-primary' : isDarkMode ? 'text-secondary' : 'text-muted'}`}>
                            Supports images, logs, or documents • Max 10MB
                          </p>
                        </div>
                      ) : (
                        /* Selected File Display */
                        <div
                          className="p-4 rounded-3"
                          style={{
                            background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                            border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <FaPaperclip className={`me-3 ${isDarkMode ? 'text-secondary' : 'text-muted'}`} />
                              <div>
                                <h6 className="mb-1">{formData.attachment.name}</h6>
                                <small className={isDarkMode ? 'text-secondary' : 'text-muted'}>
                                  {(formData.attachment.size / (1024 * 1024)).toFixed(2)} MB
                                </small>
                              </div>
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={removeFile}
                              className="rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: '40px', height: '40px' }}
                            >
                              <FaTimes />
                            </Button>
                          </div>
                        </div>
                      )}
                    </Form.Group>

                    {/* Submit Button */}
                    <div className="d-grid gap-3">
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="py-3 rounded-3 fw-semibold border-0 fs-6"
                        style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 8px 32px rgba(102, 126, 234, 0.3)';
                        }}
                      >
                        {submitting ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Submitting Report...
                          </>
                        ) : (
                          <>
                            <FaBug className="me-2" />
                            Submit Bug Report
                          </>
                        )}
                      </Button>

                      <Button
                        variant={isDarkMode ? "outline-light" : "outline-secondary"}
                        onClick={() => navigate('/')}
                        className="py-2 rounded-3 fs-6"
                        style={{
                          border: '2px solid',
                          background: 'transparent'
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Success Modal */}
      <Modal show={submitted} onHide={() => setSubmitted(false)} centered>
        <Modal.Body
          className="text-center py-5 border-0 rounded-4 position-relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <div className="position-absolute w-100 h-100 top-0 start-0 opacity-20">
            <div style={{
              background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.4) 0%, transparent 50%)',
              width: '100%',
              height: '100%'
            }}></div>
          </div>
          <FaCheckCircle size={52} className="mb-3 position-relative" />
          <h4 className="position-relative">Bug Reported!</h4>
          <p className="mb-4 position-relative">Your report has been sent for review.</p>
          <Button
            variant="light"
            onClick={() => navigate('/dashboard')}
            className="rounded-3 px-4 fw-semibold position-relative"
            style={{
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}
          >
            Go to Dashboard
          </Button>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default BugReportForm;